import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { pricingPlans } from '../mock';
import { ArrowRight, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const Iscrizione = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const standardPlans = pricingPlans.filter((plan) => !plan.requiresCall);
  const [formData, setFormData] = useState({
    studentName: '',
    studentAge: '',
    parentEmail: '',
    selectedPlan: 'monthly',
    acceptTerms: false
  });

  const handleChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const goToPayment = (enrollmentId) => {
    const params = new URLSearchParams({ plan: formData.selectedPlan });
    if (enrollmentId) {
      params.set('enrollmentId', enrollmentId);
    }
    navigate(`/pagamento?${params.toString()}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.studentName || !formData.studentAge || !formData.parentEmail) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    
    if (!formData.acceptTerms) {
      toast.error("Devi accettare i termini e condizioni per procedere");
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare data for backend
      const submitData = {
        studentName: formData.studentName,
        studentAge: parseInt(formData.studentAge, 10),
        schoolGrade: "non_specificata",
        parentName: "Non specificato",
        parentEmail: formData.parentEmail,
        parentPhone: "Non specificato",
        selectedPlan: formData.selectedPlan,
        preferredMode: "online",
        acceptTerms: true
      };
      
      const response = await axios.post(`${API}/enrollment`, submitData);
      
      if (response.data.success) {
        toast.success("Iscrizione completata! Reindirizzamento...");
        // Redirect to payment page
        setTimeout(() => {
          goToPayment(response.data.enrollmentId);
        }, 1000);
      }
    } catch (error) {
      console.error("Enrollment error:", error);
      const contentType = error.response?.headers?.["content-type"] || "";
      const backendUnavailable =
        !error.response ||
        [404, 405].includes(error.response.status) ||
        contentType.includes("text/html");

      if (backendUnavailable) {
        toast.info("Reindirizzamento al pagamento sicuro...");
        setTimeout(() => {
          goToPayment();
        }, 700);
      } else {
        toast.error(error.response?.data?.detail || "Si è verificato un errore. Riprova più tardi.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedPlanDetails = pricingPlans.find(p => p.id === formData.selectedPlan);

  return (
    <div className="enrollment-page">
      <section className="section-spacing">
        <div className="container">
          <div className="section-header">
            <h1 className="heading-1">Iscrizione al doposcuola</h1>
            <p className="body-large text-secondary">
              Compila il modulo per iscriverti a StudioInsieme
            </p>
          </div>

          <div className="enrollment-grid">
            {/* Form */}
            <Card className="enrollment-form-card">
              <CardHeader>
                <CardTitle className="heading-3">Dati per l'iscrizione</CardTitle>
                <CardDescription>Inserisci i dati dello studente</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="enrollment-form">
                  <div className="form-group">
                    <Label htmlFor="studentName">Nome e Cognome studente *</Label>
                    <Input
                      id="studentName"
                      name="studentName"
                      type="text"
                      value={formData.studentName}
                      onChange={(e) => handleChange('studentName', e.target.value)}
                      required
                      placeholder="Es: Marco Rossi"
                    />
                  </div>

                  <div className="form-group">
                    <Label htmlFor="studentAge">Età *</Label>
                    <Input
                      id="studentAge"
                      name="studentAge"
                      type="number"
                      value={formData.studentAge}
                      onChange={(e) => handleChange('studentAge', e.target.value)}
                      required
                      min="6"
                      max="18"
                      placeholder="Es: 10"
                    />
                  </div>

                  <div className="form-group">
                    <Label htmlFor="parentEmail">Email genitore *</Label>
                    <Input
                      id="parentEmail"
                      name="parentEmail"
                      type="email"
                      value={formData.parentEmail}
                      onChange={(e) => handleChange('parentEmail', e.target.value)}
                      required
                      placeholder="maria.rossi@email.com"
                    />
                  </div>

                  <div className="form-group">
                    <Label>Scegli il piano *</Label>
                    <RadioGroup
                      value={formData.selectedPlan}
                      onValueChange={(value) => handleChange('selectedPlan', value)}
                      className="plan-radio-group"
                    >
                      {standardPlans.map((plan) => (
                        <div key={plan.id} className="plan-radio-item">
                          <RadioGroupItem value={plan.id} id={plan.id} />
                          <Label htmlFor={plan.id} className="plan-radio-label">
                            <div className="plan-info">
                              <span className="plan-name">{plan.name}</span>
                              <span className="plan-price">€{plan.price}/{plan.period}</span>
                            </div>
                            {plan.savings && (
                              <span className="plan-savings">{plan.savings}</span>
                            )}
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>

                  <div className="form-group">
                    <div className="checkbox-item">
                      <Checkbox
                        id="terms"
                        checked={formData.acceptTerms}
                        onCheckedChange={(checked) => handleChange('acceptTerms', checked)}
                      />
                      <Label htmlFor="terms" className="checkbox-label">
                        Accetto i <Link to="/termini">termini e condizioni</Link>, la <Link to="/privacy">privacy policy</Link> e la <Link to="/rimborsi">politica rimborsi</Link> *
                      </Label>
                    </div>
                  </div>

                                  <p className="form-disclaimer">
                  I piani standard vengono confermati in base a disponibilità, classe, bisogni scolastici e modalità concordata. Per richieste più complesse o percorsi personalizzati, contattaci prima del pagamento: organizzeremo una call conoscitiva e invieremo una proposta su misura.
                </p>

<Button
                    type="submit"
                    className="btn-primary w-full"
                    disabled={isSubmitting || !formData.acceptTerms || !formData.studentName || !formData.studentAge || !formData.parentEmail}
                  >
                    {isSubmitting ? 'Elaborazione...' : 'Procedi al pagamento'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Summary Sidebar */}
            {selectedPlanDetails && (
              <Card className="enrollment-summary-card">
                <CardHeader>
                  <CardTitle className="heading-3">Riepilogo</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="summary-section">
                    <h4 className="body-medium">Piano selezionato</h4>
                    <div className="summary-plan">
                      <p className="plan-name">{selectedPlanDetails.name}</p>
                      <p className="plan-price">€{selectedPlanDetails.price}/{selectedPlanDetails.period}</p>
                    </div>
                  </div>

                  <div className="summary-section">
                    <h4 className="body-medium">Cosa include:</h4>
                    <ul className="feature-list-small">
                      {selectedPlanDetails.features.slice(0, 3).map((feature, index) => (
                        <li key={index} className="feature-item-small">
                          <CheckCircle className="check-icon-small" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {selectedPlanDetails.savings && (
                    <div className="savings-highlight">
                      <p className="body-small">{selectedPlanDetails.savings}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Iscrizione;
